import { getFeatureGates } from '@/lib/api-client';

import { setLiveGateClient, type GateClient } from './gate-registry';

// The gate source for the app: values come from the backend's GET /api/gates,
// not from an analytics SDK, so every user gets the same gates whatever they
// chose on the cookie banner. The client keeps the last answer in local
// storage so a reload starts from known values instead of flickering.
//
// Failure never spreads: a failed fetch keeps whatever values the client
// already holds, and with none it settles every gate to off. There is no retry
// loop; the next page load or user change fetches again.

/** Local-storage key for the cached gate values. */
export const GATE_CACHE_KEY = 'stitchpress-feature-gates';

/** Bump when the cache shape changes; older entries are then ignored. */
const GATE_CACHE_VERSION = 1;

/**
 * What the cache holds: gate values and whether they were evaluated for a
 * signed-in caller. Never a user id, so a cache left on a shared browser says
 * nothing about who wrote it.
 */
export interface CachedGates {
    signedIn: boolean;
    gates: Readonly<Record<string, boolean>>;
}

function isGateMap(v: unknown): v is Record<string, boolean> {
    return (
        typeof v === 'object' &&
        v !== null &&
        !Array.isArray(v) &&
        Object.values(v).every((value) => typeof value === 'boolean')
    );
}

/**
 * Read the cached gates. Never throws: a missing, corrupt, wrong-version or
 * non-boolean entry, or unavailable storage, all collapse to `null`.
 */
export function readGateCache(): CachedGates | null {
    try {
        const raw = window.localStorage.getItem(GATE_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Record<string, unknown> | null;
        if (parsed === null || typeof parsed !== 'object') return null;
        if (parsed.version !== GATE_CACHE_VERSION) return null;
        if (typeof parsed.signedIn !== 'boolean') return null;
        if (!isGateMap(parsed.gates)) return null;
        return { signedIn: parsed.signedIn, gates: parsed.gates };
    } catch {
        return null;
    }
}

/** Persist the gates with the current schema version. Swallows storage errors. */
export function writeGateCache(entry: CachedGates): void {
    try {
        window.localStorage.setItem(
            GATE_CACHE_KEY,
            JSON.stringify({
                version: GATE_CACHE_VERSION,
                signedIn: entry.signedIn,
                gates: entry.gates,
            }),
        );
    } catch {
        // Ignore storage failures (private mode, quota, etc.).
    }
}

/** Remove the cached gates. Swallows storage errors. */
export function clearGateCache(): void {
    try {
        window.localStorage.removeItem(GATE_CACHE_KEY);
    } catch {
        // Ignore storage failures (private mode, quota, etc.).
    }
}

export interface BackendGateClient extends GateClient {
    /**
     * Start from the cached values and become 'Ready'. A signed-in load applies
     * either kind of cache: anonymous values carry nothing user-specific, and
     * starting from them keeps rolled-out features from flashing off on the
     * first load after sign-in. A signed-out load discards a cache written
     * while signed in, so it never starts from the previous user's gates. With
     * no usable cache the client stays as it was.
     */
    seedFromCache(signedIn: boolean): void;
    /**
     * Fetch fresh gates, apply them and cache them. Only the latest call may
     * apply or cache its result; a response that has been superseded is
     * dropped. A failure keeps the current values, or, with none, settles
     * every gate to off. A failed fetch never rejects; only a throwing
     * `values_updated` listener can, so callers should still catch.
     */
    refresh(req: { signedIn: boolean; token: string | null }): Promise<void>;
}

export function createBackendGateClient(
    fetchGates: (token: string | null) => Promise<Record<string, boolean>>,
): BackendGateClient {
    let values: Readonly<Record<string, boolean>> = {};
    let status: 'Loading' | 'Ready' = 'Loading';
    let latestRequest = 0;
    const listeners = new Set<() => void>();

    const emit = () => {
        for (const cb of [...listeners]) cb();
    };

    return {
        get loadingStatus() {
            return status;
        },

        checkGate(name) {
            return values[name] === true;
        },

        on(_event, cb) {
            listeners.add(cb);
        },

        off(_event, cb) {
            listeners.delete(cb);
        },

        seedFromCache(signedIn) {
            const cached = readGateCache();
            if (cached === null) return;
            if (cached.signedIn && !signedIn) {
                clearGateCache();
                return;
            }
            values = cached.gates;
            status = 'Ready';
            emit();
        },

        async refresh({ signedIn, token }) {
            const request = ++latestRequest;
            let gates: Record<string, boolean>;
            try {
                gates = await fetchGates(token);
            } catch {
                if (request !== latestRequest) return;
                // Keep what we have. With nothing, settle every gate off
                // rather than leave status-aware callers waiting forever.
                if (status !== 'Ready') {
                    status = 'Ready';
                    emit();
                }
                return;
            }
            if (request !== latestRequest) return;
            values = gates;
            status = 'Ready';
            writeGateCache({ signedIn, gates });
            emit();
        },
    };
}

/** Create the backend-backed client and register it as the app's gate source. */
export function installBackendGates(): BackendGateClient {
    const client = createBackendGateClient(getFeatureGates);
    setLiveGateClient(client);
    return client;
}
